<template>
  <div class="bg-white overflow-x-hidden">
    <NavbarComponent />
    <div class="h-20"></div>

    <!-- ==================== HERO SECTION ==================== -->
    <section class="min-h-[90vh] flex items-center py-12 md:py-0">
      <div class="w-[90%] mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div class="space-y-6 text-center lg:text-left order-2 lg:order-1">
            <h1 class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              <span class="block animate-slide-up" style="animation-delay: 0.1s;">{{ $t('hero.title') }}</span>
              <span class="block text-[#009B9D] animate-slide-up" style="animation-delay: 0.2s;">{{ $t('hero.titleHighlight') }}</span>
            </h1>
            <p class="text-gray-600 text-base md:text-lg max-w-lg mx-auto lg:mx-0 animate-fade-in" style="animation-delay: 0.3s;">
              {{ $t('hero.description') }}
            </p>
            <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in" style="animation-delay: 0.4s;">
              <a :href="adminUrl" target="_blank" rel="noopener" class="group relative inline-flex items-center justify-center px-6 py-3.5 bg-[#009B9D] text-white font-semibold rounded-xl shadow-lg shadow-[#009B9D]/30 hover:shadow-xl hover:shadow-[#009B9D]/40 hover:-translate-y-1 transition-all duration-300">
                <span class="relative z-10">{{ $t('hero.startTrial') }}</span>
              </a>
              <a href="#features" class="inline-flex items-center justify-center px-6 py-3.5 border-2 border-[#009B9D] text-[#009B9D] font-semibold rounded-xl hover:bg-[#009B9D] hover:text-white transition-all duration-300">
                <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"/></svg>
                {{ $t('hero.watchDemo') }}
              </a>
            </div>
            <div class="flex justify-center lg:justify-start gap-6 md:gap-10 pt-6 animate-fade-in" style="animation-delay: 0.5s;">
              <div class="text-center">
                <div class="text-2xl md:text-3xl font-bold text-[#009B9D]">50+</div>
                <div class="text-sm text-gray-600 font-medium">{{ $t('hero.activePressings') }}</div>
              </div>
              <div class="text-center">
                <div class="text-2xl md:text-3xl font-bold text-[#009B9D]">10K+</div>
                <div class="text-sm text-gray-600 font-medium">{{ $t('hero.ordersManaged') }}</div>
              </div>
              <div class="text-center">
                <div class="text-2xl md:text-3xl font-bold text-[#009B9D]">98%</div>
                <div class="text-sm text-gray-600 font-medium">{{ $t('hero.satisfaction') }}</div>
              </div>
            </div>
          </div>
          <div class="relative order-1 lg:order-2 flex justify-center items-center h-[300px] sm:h-[400px] lg:h-[600px]">
            <div class="relative w-48 h-48 sm:w-64 sm:h-64 lg:w-80 lg:h-80 rounded-full overflow-hidden shadow-2xl animate-scale-in z-20">
              <img src="/images/one.jpg" alt="Pressing professionnel" class="w-full h-full object-cover" />
            </div>
            <div class="hidden lg:block">
              <div class="absolute top-0 left-8 w-16 h-16 rounded-full overflow-hidden shadow-lg animate-float z-10"><img src="/images/five.jpg" alt="" class="w-full h-full object-cover" /></div>
              <div class="absolute top-4 right-24 w-10 h-10 rounded-full overflow-hidden shadow-lg animate-float z-10" style="animation-delay: 0.2s;"><img src="/images/three.jpg" alt="" class="w-full h-full object-cover" /></div>
              <div class="absolute top-0 right-4 w-20 h-20 rounded-full overflow-hidden shadow-lg animate-float z-10" style="animation-delay: 0.3s;"><img src="/images/three.jpg" alt="" class="w-full h-full object-cover" /></div>
              <div class="absolute right-0 top-1/2 transform translate-x-4 -translate-y-1/2 w-14 h-14 rounded-full overflow-hidden shadow-lg animate-float z-10" style="animation-delay: 0.4s;"><img src="/images/two.jpg" alt="" class="w-full h-full object-cover" /></div>
            </div>
            <div class="absolute -bottom-10 -left-10 w-40 h-40 bg-[#009B9D]/10 rounded-full blur-3xl"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== FEATURES SECTION ==================== -->
    <section class="py-16 md:py-24 bg-gray-50/50" id="features">
      <div class="w-[90%] mx-auto">
        <div class="text-center space-y-4 mb-12">
          <h2 class="text-2xl md:text-4xl font-bold text-gray-900">{{ $t('features.title') }}</h2>
          <div class="flex justify-center"><div class="h-1 w-16 bg-[#009B9D] rounded-full"></div></div>
          <p class="text-gray-600 max-w-2xl mx-auto">{{ $t('features.subtitle') }}</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="(feature, index) in featuresList" :key="index" class="group bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 border border-gray-100">
            <div class="w-14 h-14 bg-[#009B9D]/10 rounded-2xl flex items-center justify-center mb-5 group-hover:bg-[#009B9D] transition-colors duration-300">
              <div class="text-[#009B9D] group-hover:text-white transition-colors duration-300" v-html="feature.icon"></div>
            </div>
            <h3 class="text-lg font-bold text-gray-900 mb-3">{{ $t(feature.titleKey) }}</h3>
            <p class="text-gray-600 text-sm leading-relaxed">{{ $t(feature.descKey) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== HOW IT WORKS - CLIENT APP ==================== -->
    <HowItWork />

    <!-- ==================== HOW IT WORKS - ADMIN APP ==================== -->
    <HowItWorkAdmin />

    <!-- ==================== APP SHOWCASE SECTION ==================== -->
    <section class="bg-[#009B9D] py-16 md:py-24" id="apps">
      <div class="w-[90%] mx-auto">
        <div class="text-center space-y-4 mb-12">
          <h2 class="text-2xl md:text-4xl font-bold text-white">{{ $t('appShowcase.title') }}</h2>
          <p class="text-white/80 max-w-xl mx-auto">{{ $t('appShowcase.subtitle') }}</p>
        </div>
        <div class="relative flex justify-center items-center h-[400px] md:h-[520px] mb-12">
          <!-- Left Phone -->
          <div class="hidden md:block absolute left-1/2 transform -translate-x-[150%] z-10">
            <div class="w-40 h-[340px] bg-[#1a3a3b] rounded-[2.5rem] p-2.5 shadow-2xl opacity-90 hover:opacity-100 transition-opacity duration-300">
              <div class="w-full h-full bg-white rounded-[2rem] overflow-hidden">
                <img src="/images/pickup.png" alt="App Gestion" class="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
          <!-- Center Phone -->
          <div class="relative z-20">
            <div class="w-52 h-[420px] bg-[#1a3a3b] rounded-[3rem] p-3 shadow-2xl">
              <div class="w-full h-full bg-white rounded-[2.2rem] overflow-hidden">
                <img src="/images/place_order.png" alt="App Client" class="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
          <!-- Right Phone -->
          <div class="hidden md:block absolute left-1/2 transform translate-x-[50%] z-10">
            <div class="w-40 h-[340px] bg-[#1a3a3b] rounded-[2.5rem] p-2.5 shadow-2xl opacity-90 hover:opacity-100 transition-opacity duration-300">
              <div class="w-full h-full bg-white rounded-[2rem] overflow-hidden">
                <img src="/images/cleaning.png" alt="App Livreur" class="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-col sm:flex-row justify-center gap-4">
          <a href="#" @click.prevent="showComingSoonDialog = true" class="inline-flex items-center justify-center space-x-3 bg-white px-6 py-4 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
            <svg class="w-6 h-6 text-[#009B9D]" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            <div class="text-left">
              <span class="block text-xs text-gray-500">{{ $t('appShowcase.appStore') }}</span>
              <span class="block text-sm font-bold text-[#009B9D]">App Store</span>
            </div>
          </a>
          <a href="#" @click.prevent="showComingSoonDialog = true" class="inline-flex items-center justify-center space-x-3 bg-white px-6 py-4 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
            <svg class="w-6 h-6 text-[#009B9D]" viewBox="0 0 24 24" fill="currentColor"><path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/></svg>
            <div class="text-left">
              <span class="block text-xs text-gray-500">{{ $t('appShowcase.playStore') }}</span>
              <span class="block text-sm font-bold text-[#009B9D]">Google Play</span>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- ==================== DELIVERY APP SECTION (Coming Soon) ==================== -->
    <DeliveryAppSection />

    <!-- ==================== BENEFITS SECTION ==================== -->
    <section class="py-16 md:py-24">
      <div class="w-[90%] mx-auto">
        <div class="text-center space-y-4 mb-12">
          <h2 class="text-2xl md:text-4xl font-bold text-gray-900">{{ $t('benefits.title') }}</h2>
          <div class="flex justify-center"><div class="h-1 w-16 bg-[#009B9D] rounded-full"></div></div>
          <p class="text-gray-600 max-w-2xl mx-auto">{{ $t('benefits.subtitle') }}</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="(benefit, key) in benefits" :key="key" class="text-center p-6">
            <div class="w-16 h-16 mx-auto bg-[#009B9D]/10 rounded-2xl flex items-center justify-center mb-4">
              <div class="text-[#009B9D]" v-html="benefit.icon"></div>
            </div>
            <h3 class="font-bold text-gray-900 mb-2">{{ $t(`benefits.items.${key}.title`) }}</h3>
            <p class="text-gray-600 text-sm">{{ $t(`benefits.items.${key}.description`) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== TESTIMONIALS SECTION ==================== -->
    <section class="py-16 md:py-24 bg-gray-50" id="testimonials">
      <div class="w-[90%] mx-auto">
        <div class="text-center space-y-4 mb-12">
          <h2 class="text-2xl md:text-4xl font-bold text-gray-900">{{ $t('testimonials.title') }}</h2>
          <div class="flex justify-center"><div class="h-1 w-16 bg-[#009B9D] rounded-full"></div></div>
          <p class="text-gray-600 max-w-2xl mx-auto">{{ $t('testimonials.subtitle') }}</p>
        </div>
        <SwiperComponent />
      </div>
    </section>

    <!-- ==================== PRICING SECTION ==================== -->
    <section class="py-16 md:py-24" id="pricing">
      <div class="w-[90%] mx-auto">
        <div class="text-center space-y-4 mb-12">
          <h2 class="text-2xl md:text-4xl font-bold text-gray-900">{{ $t('pricing.title') }}</h2>
          <div class="flex justify-center"><div class="h-1 w-16 bg-[#009B9D] rounded-full"></div></div>
          <p class="text-gray-600 max-w-2xl mx-auto">{{ $t('pricing.subtitle') }}</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          <!-- Basic Plan -->
          <div class="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
            <div class="text-center space-y-4">
              <h3 class="text-xl font-bold text-gray-900">{{ $t('pricing.basic.title') }}</h3>
              <div class="text-[#009B9D]">
                <span class="text-4xl font-bold">{{ $t('pricing.basic.price') }}</span>
                <span class="text-gray-500 text-sm">{{ $t('pricing.perMonth') }}</span>
              </div>
              <p class="text-gray-600 text-sm">{{ $t('pricing.basic.description') }}</p>
              <div class="h-px bg-gray-100"></div>
              <ul class="space-y-3 text-left">
                <li v-for="(feature, i) in $tm('pricing.basic.features')" :key="i" class="flex items-start space-x-3">
                  <svg class="w-5 h-5 text-[#009B9D] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                  <span class="text-gray-700 text-sm">{{ feature }}</span>
                </li>
              </ul>
              <button @click="goToAdmin" class="w-full py-3 border-2 border-[#009B9D] text-[#009B9D] font-semibold rounded-full hover:bg-[#009B9D] hover:text-white transition-all duration-300">
                {{ $t('pricing.basic.cta') }}
              </button>
            </div>
          </div>
          
          <!-- Boost Plan (Popular) -->
          <div class="relative bg-white rounded-3xl p-8 shadow-xl border-2 border-[#009B9D] transform md:-translate-y-4">
            <div class="absolute -top-4 left-1/2 -translate-x-1/2">
              <span class="bg-[#009B9D] text-white text-xs font-bold px-4 py-1 rounded-full">{{ $t('pricing.mostPopular') }}</span>
            </div>
            <div class="text-center space-y-4">
              <h3 class="text-xl font-bold text-gray-900">{{ $t('pricing.boost.title') }}</h3>
              <div class="text-[#009B9D]">
                <span class="text-4xl font-bold">{{ $t('pricing.boost.price') }}</span>
                <span class="text-gray-500 text-sm">{{ $t('pricing.perMonth') }}</span>
              </div>
              <p class="text-gray-600 text-sm">{{ $t('pricing.boost.description') }}</p>
              <div class="h-px bg-gray-100"></div>
              <ul class="space-y-3 text-left">
                <li v-for="(feature, i) in $tm('pricing.boost.features')" :key="i" class="flex items-start space-x-3">
                  <svg class="w-5 h-5 text-[#009B9D] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                  <span class="text-gray-700 text-sm">{{ feature }}</span>
                </li>
              </ul>
              <button @click="goToAdmin" class="w-full py-3 bg-[#009B9D] text-white font-semibold rounded-full shadow-lg shadow-[#009B9D]/30 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
                {{ $t('pricing.boost.cta') }}
              </button>
            </div>
          </div>
          
          <!-- Premium Plan -->
          <div class="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
            <div class="text-center space-y-4">
              <h3 class="text-xl font-bold text-gray-900">{{ $t('pricing.premium.title') }}</h3>
              <div class="text-[#009B9D]">
                <span class="text-4xl font-bold">{{ $t('pricing.premium.price') }}</span>
                <span class="text-gray-500 text-sm">{{ $t('pricing.perMonth') }}</span>
              </div>
              <p class="text-gray-600 text-sm">{{ $t('pricing.premium.description') }}</p>
              <div class="h-px bg-gray-100"></div>
              <ul class="space-y-3 text-left">
                <li v-for="(feature, i) in $tm('pricing.premium.features')" :key="i" class="flex items-start space-x-3">
                  <svg class="w-5 h-5 text-[#009B9D] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                  <span class="text-gray-700 text-sm">{{ feature }}</span>
                </li>
              </ul>
              <button @click="contactTeam" class="w-full py-3 border-2 border-[#009B9D] text-[#009B9D] font-semibold rounded-full hover:bg-[#009B9D] hover:text-white transition-all duration-300">
                {{ $t('pricing.premium.cta') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== CTA SECTION ==================== -->
    <section class="py-16 md:py-24 bg-[#009B9D]/5" id="contact">
      <div class="w-[90%] max-w-3xl mx-auto text-center space-y-6">
        <h2 class="text-2xl md:text-4xl font-bold text-gray-900">
          {{ $t('cta.title') }} <span class="text-[#009B9D]">{{ $t('cta.titleHighlight') }}</span>
        </h2>
        <p class="text-gray-600">{{ $t('cta.subtitle') }}</p>
        <a href="#" @click.prevent="openWhatsAppDemo" class="inline-block bg-[#009B9D] text-white px-8 py-4 rounded-full font-semibold shadow-lg shadow-[#009B9D]/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          {{ $t('cta.button') }}
        </a>
        <div class="flex flex-wrap justify-center gap-6 pt-4">
          <div class="flex items-center space-x-2 text-gray-600">
            <svg class="w-5 h-5 text-[#009B9D]" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <span class="text-sm">{{ $t('cta.features.freeSetup') }}</span>
          </div>
          <div class="flex items-center space-x-2 text-gray-600">
            <svg class="w-5 h-5 text-[#009B9D]" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <span class="text-sm">{{ $t('cta.features.noCommitment') }}</span>
          </div>
          <div class="flex items-center space-x-2 text-gray-600">
            <svg class="w-5 h-5 text-[#009B9D]" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
            <span class="text-sm">{{ $t('cta.features.cancelAnytime') }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== FOOTER ==================== -->
    <footer class="bg-gray-900 py-16">
      <div class="w-[90%] mx-auto">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <h3 class="text-white text-xl font-bold mb-4">{{ $t('footer.about.title') }}</h3>
            <p class="text-gray-400 text-sm leading-relaxed">{{ $t('footer.about.description') }}</p>
          </div>
          <div>
            <h3 class="text-white text-xl font-bold mb-4">{{ $t('footer.quickLinks.title') }}</h3>
            <ul class="space-y-2">
              <li><a href="#" class="text-gray-400 hover:text-[#009B9D] transition-colors text-sm">{{ $t('nav.home') }}</a></li>
              <li><a href="#features" class="text-gray-400 hover:text-[#009B9D] transition-colors text-sm">{{ $t('nav.features') }}</a></li>
              <li><a href="#pricing" class="text-gray-400 hover:text-[#009B9D] transition-colors text-sm">{{ $t('nav.pricing') }}</a></li>
              <li><a href="#how-it-works" class="text-gray-400 hover:text-[#009B9D] transition-colors text-sm">{{ $t('nav.howItWorks') }}</a></li>
              <li><a href="#testimonials" class="text-gray-400 hover:text-[#009B9D] transition-colors text-sm">{{ $t('nav.testimonials') }}</a></li>
            </ul>
          </div>
          <div>
            <h3 class="text-white text-xl font-bold mb-4">{{ $t('footer.connect.title') }}</h3>
            <div class="flex space-x-3">
              <a href="#" class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#009B9D] transition-colors">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="#" class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#009B9D] transition-colors">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="#" class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#009B9D] transition-colors">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
          </div>
        </div>
        <div class="border-t border-gray-800 mt-12 pt-8 text-center">
          <p class="text-gray-500 text-sm">{{ $t('footer.copyright') }}</p>
        </div>
      </div>
    </footer>

    <!-- Coming Soon Dialog -->
    <teleport to="body">
      <transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="showComingSoonDialog" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showComingSoonDialog = false"></div>
          <div class="relative bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl text-center">
            <button @click="showComingSoonDialog = false" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
            <div class="w-20 h-20 mx-auto bg-[#009B9D]/10 rounded-full flex items-center justify-center mb-6">
              <svg class="w-10 h-10 text-[#009B9D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-3">{{ $t('dialog.comingSoon.title') }}</h3>
            <p class="text-gray-600 mb-8">{{ $t('dialog.comingSoon.message') }}</p>
            <button @click="showComingSoonDialog = false" class="bg-[#009B9D] text-white px-8 py-3 rounded-full font-semibold shadow-lg shadow-[#009B9D]/30 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
              {{ $t('dialog.comingSoon.close') }}
            </button>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import NavbarComponent from './NavbarComponent.vue'
import HowItWork from '@/components/HowItWork.vue'
import HowItWorkAdmin from '@/components/HowItWorkAdmin.vue'
import SwiperComponent from '@/components/SwiperComponent.vue'
import DeliveryAppSection from '@/components/DeliveryAppSection.vue'

export default {
  name: 'HomeView',
  components: { NavbarComponent, HowItWork, HowItWorkAdmin, SwiperComponent, DeliveryAppSection },
  data() {
    return {
      showComingSoonDialog: false,
      featuresList: [
        { titleKey: 'features.managementApp.title', descKey: 'features.managementApp.description', icon: '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/></svg>' },
        { titleKey: 'features.clientApp.title', descKey: 'features.clientApp.description', icon: '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>' },
        { titleKey: 'features.deliveryApp.title', descKey: 'features.deliveryApp.description', icon: '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"/></svg>' },
        { titleKey: 'features.integrations.title', descKey: 'features.integrations.description', icon: '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"/></svg>' }
      ],
      benefits: {
        time: { icon: '<svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>' },
        customers: { icon: '<svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>' },
        revenue: { icon: '<svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>' },
        support: { icon: '<svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>' }
      }
    }
  },
  computed: {
    adminUrl() {
      const hostname = window.location.hostname
      if (hostname === 'localhost' || hostname.includes('dev')) {
        return 'https://dev.admin.kline.to'
      }
      return 'https://admin.kline.to'
    }
  },
  methods: {
    goToAdmin() {
      window.open(this.adminUrl, '_blank')
    },
    openWhatsAppDemo() {
      const message = encodeURIComponent('Bonjour, je souhaite démarrer mon essai gratuit de Kline.')
      window.open(`https://wa.me/237695496806?text=${message}`, '_blank')
    },
    contactTeam() {
      const message = encodeURIComponent("Bonjour, je suis intéressé par l'offre Premium de Kline.")
      window.open(`https://wa.me/237682696414?text=${message}`, '_blank')
    }
  }
}
</script>

<style>
@keyframes slide-up { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
@keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes scale-in { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
@keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
.animate-slide-up { animation: slide-up 0.8s ease-out forwards; opacity: 0; }
.animate-fade-in { animation: fade-in 0.8s ease-out forwards; opacity: 0; }
.animate-scale-in { animation: scale-in 0.8s ease-out forwards; }
.animate-float { animation: float 3s ease-in-out infinite; }
html { scroll-behavior: smooth; }
</style>